import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOutlet } from '../contexts/OutletContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  Users,
  RefreshCw,
  Loader2,
  QrCode,
  Download,
  Printer,
} from 'lucide-react';

// Status config will use translations dynamically
const getStatusConfig = (t) => ({
  AVAILABLE: { label: t('tables.status.available'), class: 'table-available' },
  OCCUPIED: { label: t('tables.status.occupied'), class: 'table-occupied' },
  RESERVED: { label: t('tables.status.reserved'), class: 'table-reserved' },
  CLEANING: { label: t('tables.status.cleaning'), class: 'table-cleaning' },
});

export default function Tables() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const { currentOutlet } = useOutlet();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrImageLoaded, setQrImageLoaded] = useState(false);
  const [qrImageError, setQrImageError] = useState(false);
  const [printingQr, setPrintingQr] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [formData, setFormData] = useState({
    number: '',
    capacity: 4,
    status: 'AVAILABLE',
  });

  // Initialize STATUS_CONFIG with translation support
  const STATUS_CONFIG = getStatusConfig(t);

  // Get QR code for table
  const handleShowQr = async (table) => {
    setQrLoading(true);
    setQrImageLoaded(false);
    setQrImageError(false);
    try {
      const resp = await api.get(`/tables/${table.id}/qr-data`);
      setQrData({
        ...resp.data,
        qrImageUrl: `${process.env.REACT_APP_BACKEND_URL}/api/tables/${table.id}/qr`
      });
      setShowQrDialog(true);
    } catch (err) {
      toast.error('Failed to generate QR code');
    } finally {
      setQrLoading(false);
    }
  };

  // Download QR code
  const downloadQr = async () => {
    if (!qrData) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tables/${qrData.table_id}/qr`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `table-${qrData.table_number}-qr.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('QR Code downloaded');
    } catch (err) {
      toast.error('Failed to download QR code');
    }
  };

  // Print QR code using thermal print HTML
  const printQr = () => {
    if (!qrData || printingQr) return;
    setPrintingQr(true);
    
    // Open print window with thermal-optimized HTML
    const printUrl = `${process.env.REACT_APP_BACKEND_URL}/api/tables/${qrData.table_id}/qr/print?autoprint=1`;
    const printWindow = window.open(printUrl, '_blank', 'width=300,height=250,menubar=no,toolbar=no,location=no,status=no');
    
    if (printWindow) {
      toast.success(`Opening print dialog for table ${qrData.table_number}`);
      
      // Monitor window close
      const checkWindow = setInterval(() => {
        if (printWindow.closed) {
          clearInterval(checkWindow);
          setPrintingQr(false);
        }
      }, 500);
      
      // Timeout fallback
      setTimeout(() => {
        clearInterval(checkWindow);
        setPrintingQr(false);
      }, 30000);
    } else {
      toast.error('Popup blocked. Please allow popups for printing.');
      setPrintingQr(false);
    }
  };

  const fetchTables = useCallback(async () => {
    if (!currentOutlet?.id) return;
    setLoading(true);
    try {
      const resp = await api.get('/tables', { params: { outlet_id: currentOutlet.id } });
      setTables(resp.data);
    } catch (error) {
      toast.error(t('messages.error.failed_fetch_tables'));
    } finally {
      setLoading(false);
    }
  }, [api, currentOutlet?.id, t]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  const handleAddTable = async () => {
    try {
      await api.post('/tables', {
        ...formData,
        outlet_id: currentOutlet.id,
      });
      toast.success(t('messages.success.table_added'));
      setShowAddDialog(false);
      setFormData({ number: '', capacity: 4, status: 'AVAILABLE' });
      fetchTables();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.error.failed_add_table'));
    }
  };

  const handleUpdateTable = async () => {
    if (!selectedTable) return;
    try {
      await api.put(`/tables/${selectedTable.id}`, formData);
      toast.success(t('messages.success.table_updated'));
      setShowEditDialog(false);
      setSelectedTable(null);
      fetchTables();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.error.failed_update_table'));
    }
  };

  const openEditDialog = (table) => {
    setSelectedTable(table);
    setFormData({
      number: table.number,
      capacity: table.capacity,
      status: table.status,
    });
    setShowEditDialog(true);
  };

  const handleQuickStatusChange = async (table, newStatus) => {
    try {
      await api.put(`/tables/${table.id}`, { status: newStatus });
      fetchTables();
      toast.success(t('messages.success.table_status_updated', { number: table.number }));
    } catch (error) {
      toast.error(t('messages.error.failed_update_status'));
    }
  };

  const canManage = ['OWNER', 'ADMIN', 'MANAGER'].includes(user?.role);

  if (!currentOutlet) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <p className="text-muted-foreground">{t('common.select_outlet')}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="tables-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('tables.title')}</h1>
          <p className="text-muted-foreground">{t('tables.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchTables} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
          {canManage && (
            <Button onClick={() => setShowAddDialog(true)} data-testid="add-table-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('tables.add_table')}
            </Button>
          )}
        </div>
      </div>

      {/* Status Legend */}
      <div className="flex flex-wrap gap-4">
        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
          <div key={key} className="flex items-center gap-2">
            <div className={`w-4 h-4 rounded border-2 ${config.class}`} />
            <span className="text-sm">{config.label}</span>
          </div>
        ))}
      </div>

      {/* Tables Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : tables.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t('empty_states.no_tables')}</p>
            {canManage && (
              <Button className="mt-4" onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t('tables.add_table')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {tables.map((table) => (
            <Card
              key={table.id}
              className={`cursor-pointer transition-all hover:scale-105 border-2 ${STATUS_CONFIG[table.status]?.class || ''}`}
              onClick={() => canManage && openEditDialog(table)}
              data-testid={`table-${table.number}`}
            >
              <CardContent className="p-4 text-center">
                <h3 className="text-2xl font-bold font-mono">{table.number}</h3>
                <div className="flex items-center justify-center gap-1 mt-2 text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span>{table.capacity}</span>
                </div>
                <Badge variant="outline" className={`mt-2 ${STATUS_CONFIG[table.status]?.class || ''}`}>
                  {STATUS_CONFIG[table.status]?.label || table.status}
                </Badge>

                {/* Quick Actions */}
                {table.status === 'CLEANING' && (
                  <Button
                    size="sm"
                    className="mt-3 w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuickStatusChange(table, 'AVAILABLE');
                    }}
                  >
                    {t('tables.status.available')}
                  </Button>
                )}
                
                {/* QR Code Button */}
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2 w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShowQr(table);
                  }}
                  data-testid={`qr-btn-${table.number}`}
                >
                  <QrCode className="h-4 w-4 mr-1" />
                  QR Code
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Table Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('tables.add_new_table')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('tables.table_number_label')}</Label>
              <Input
                value={formData.number}
                onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                placeholder={t('tables.table_number_placeholder')}
                data-testid="table-number-input"
              />
            </div>
            <div>
              <Label>{t('tables.capacity')}</Label>
              <Input
                type="number"
                min={1}
                max={20}
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
                data-testid="table-capacity-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleAddTable} data-testid="save-table-btn">{t('tables.add_table')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Table Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('tables.edit_table', { number: selectedTable?.number })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('tables.table_number_label')}</Label>
              <Input
                value={formData.number}
                onChange={(e) => setFormData({ ...formData, number: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('tables.capacity')}</Label>
              <Input
                type="number"
                min={1}
                max={20}
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
              />
            </div>
            <div>
              <Label>{t('tables.status_label')}</Label>
              <Select
                value={formData.status}
                onValueChange={(val) => setFormData({ ...formData, status: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleUpdateTable}>{t('tables.save_changes')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQrDialog} onOpenChange={(open) => {
        setShowQrDialog(open);
        if (!open) {
          setQrImageLoaded(false);
          setQrImageError(false);
        }
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              QR Code - {t('tables.table_number', { number: qrData?.table_number })}
            </DialogTitle>
          </DialogHeader>
          
          {qrData && (
            <div className="space-y-4">
              {/* QR Image Container */}
              <div className="bg-white p-4 rounded-lg flex items-center justify-center min-h-[280px]">
                {qrImageError ? (
                  <div className="text-center text-destructive">
                    <QrCode className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Gagal memuat QR Code</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={() => {
                        setQrImageError(false);
                        setQrImageLoaded(false);
                      }}
                    >
                      Coba Lagi
                    </Button>
                  </div>
                ) : (
                  <>
                    {!qrImageLoaded && (
                      <div className="absolute">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    <img 
                      src={qrData.qrImageUrl} 
                      alt={`QR Code for Table ${qrData.table_number}`}
                      className={`w-64 h-64 ${qrImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                      onLoad={() => setQrImageLoaded(true)}
                      onError={() => setQrImageError(true)}
                    />
                  </>
                )}
              </div>
              
              <div className="text-center text-sm text-muted-foreground">
                <p>Pelanggan dapat scan QR ini untuk</p>
                <p>memesan langsung dari meja mereka</p>
              </div>
              
              <div className="bg-muted rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Link pesanan:</p>
                <p className="text-xs font-mono break-all">{qrData.qr_url}</p>
              </div>
              
              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={downloadQr} variant="outline" data-testid="download-qr-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                <Button 
                  onClick={printQr} 
                  disabled={printingQr}
                  data-testid="print-qr-btn"
                >
                  {printingQr ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Printer className="h-4 w-4 mr-2" />
                  )}
                  {printingQr ? 'Printing...' : 'Print QR'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
